import React, { useCallback, useEffect, useRef, useState } from 'react';
import { InputLabel, Select as MUISelect, MenuItem, SelectChangeEvent, TextField, ListSubheader } from '@mui/material';
import { SxProps } from '@mui/system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Option } from '../../types';
import { debounce } from '@mui/material/utils';

export interface SelectProps {
    label: string;
    options: {
        value: SelectProps['value'];
        title: string;
    }[];
    sx?: SxProps;
    onChange: (name: string, value: SelectProps['value']) => void;
    name: string;
    value: Option;
    showSearch?: boolean;
    searchPlaceholder?: string;
}

const Select: React.FC<SelectProps> = ({
    onChange,
    name,
    value,
    label,
    options,
    sx = {},
    showSearch = false,
    searchPlaceholder,
}) => {
    const [selectWidth, setSelectWidth] = useState(0);
    const selectRef = useRef<HTMLSelectElement | null>(null);
    const [searchText, setSearchText] = useState('');
    const displayedOptions = options.filter((option) => option.title?.toLowerCase().includes(searchText.toLowerCase()));
    const selectedValue = options.find((option) => option.value === value);

    const onSelectChange = useCallback(
        (e: SelectChangeEvent) => {
            onChange(name, e.target.value);
        },
        [name, onChange]
    );

    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const setSelectOptionsWidth = debounce(() => {
        if (selectRef.current) {
            setSelectWidth(selectRef.current.offsetWidth);
        }
    }, 100);

    useEffect(() => {
        setSelectOptionsWidth();

        window.addEventListener('resize', setSelectOptionsWidth);
        return () => {
            window.removeEventListener('resize', setSelectOptionsWidth);
        };
    }, [selectRef, setSelectOptionsWidth]);

    return (
        <>
            <InputLabel
                sx={{
                    color: 'common.grey1',
                    fontSize: '14px',
                    '&.Mui-focused': {
                        color: 'common.grey1',
                    },
                }}
            >
                {label}
            </InputLabel>
            <MUISelect
                ref={selectRef}
                MenuProps={{
                    autoFocus: !showSearch,
                    sx: {
                        '& .MuiPaper-root': {
                            maxHeight: '256px',
                            maxWidth: selectWidth ? `${selectWidth}px` : 'initial',
                        },
                        '& .MuiMenuItem-root': {
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            display: 'block',
                        },
                    },
                }}
                onClose={() => setSearchText('')}
                onChange={onSelectChange}
                value={String(value) || 'none'}
                fullWidth
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                    fontSize: '14px',
                    maxHeight: '256px',
                    '&.MuiOutlinedInput-root': {
                        height: '48px',
                        '& fieldset': {
                            borderColor: 'common.grey2',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: 'common.grey1',
                        },
                        '&.Mui-focused fieldset': {
                            borderWidth: '1px',
                            borderColor: 'common.primary',
                        },
                    },
                    '&.MuiSelect-select': {
                        fontSize: '14px',
                        padding: '10px 26px 10px 12px',
                    },
                    '& .MuiSelect-icon': {
                        color: 'common.grey1',
                    },
                    '& .MuiList-root': {
                        maxHeight: '256px',
                    },
                    ...sx,
                }}
                renderValue={() => selectedValue?.title}
            >
                {showSearch ? (
                    <ListSubheader>
                        <TextField
                            autoFocus
                            onChange={onSearchChange}
                            onKeyDown={(e) => {
                                if (e.key !== 'Escape') {
                                    e.stopPropagation();
                                }
                            }}
                            placeholder={searchPlaceholder}
                            sx={{
                                fontSize: '14px',
                                color: 'common.grey1',
                                width: '100%',
                                padding: '24px 6px 12px',
                                '& .MuiOutlinedInput-root': {
                                    height: '48px',
                                    fontSize: '14px',
                                    color: 'common.grey1',
                                    '& fieldset': {
                                        borderColor: 'common.grey2',
                                        borderWidth: '1px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'common.grey1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderWidth: '1px',
                                        borderColor: 'common.primary',
                                    },
                                },
                            }}
                        />
                    </ListSubheader>
                ) : null}
                {displayedOptions.map((item, index) => (
                    <MenuItem key={index} sx={{ fontSize: '14px' }} value={item.value as string | number}>
                        {item.title}
                    </MenuItem>
                ))}
            </MUISelect>
        </>
    );
};

export default Select;
